// Kalshi API Client
// Public API for market data, authenticated API for trading

import { KalshiMarket, KalshiAPIResponse } from './types';

const TRADING_BASE_URL = 'https://trading-api.kalshi.com/trade-api/v2';

// Cloudflare Worker proxy URL
// In production, uses the deployed worker URL
const getWorkerUrl = (): string => {
  if (typeof window !== 'undefined') {
    // Use the deployed worker URL
    // In production, this is the worker's public URL
    const workerUrl = (window as any).__KALSHI_WORKER_URL__ || 'https://kalshi-usl-proxy.sparsesupernova.workers.dev';
    return workerUrl;
  }
  return 'https://kalshi-usl-proxy.sparsesupernova.workers.dev';
};

interface KalshiCredentials {
  email: string;
  password: string;
}

interface AuthToken {
  token: string;
  userId: string;
  expiresAt: Date;
}

let authToken: AuthToken | null = null;

/**
 * Fetch markets from Kalshi public API
 * Always uses worker proxy to avoid CORS issues
 */
export async function fetchMarkets(
  status: 'open' | 'closed' | 'settled' = 'open',
  limit: number = 200,
  cursor?: string
): Promise<{ markets: KalshiMarket[]; cursor?: string }> {
  // Always use worker proxy to avoid CORS issues in browser
  const workerUrl = getWorkerUrl();
  let url = `${workerUrl}/api/public/markets?status=${status}&limit=${limit}`;
  if (cursor) {
    url += `&cursor=${cursor}`;
  }

  const response = await fetch(url);
  
  if (!response.ok) {
    throw new Error(`Kalshi API error: ${response.status} ${response.statusText}`);
  }

  const data: KalshiAPIResponse<KalshiMarket> = await response.json();
  
  return {
    markets: data.markets || [],
    cursor: data.cursor,
  };
}

/**
 * Fetch all open markets, sorted by soonest expiration
 * CRITICAL: Kalshi API returns markets sorted by FURTHEST expiration first!
 * We need to fetch all pages and sort client-side to get short-term markets.
 */
export async function fetchAllOpenMarkets(maxPages: number = 50): Promise<KalshiMarket[]> {
  const allMarkets: KalshiMarket[] = [];
  let cursor: string | undefined;
  
  console.log('📊 Fetching all markets (this may take a moment)...');
  
  for (let page = 0; page < maxPages; page++) {
    const { markets, cursor: nextCursor } = await fetchMarkets('open', 200, cursor);
    allMarkets.push(...markets);
    
    // Log every 5 pages to reduce spam
    if ((page + 1) % 5 === 0) {
      console.log(`📊 Fetched ${page + 1} pages (${allMarkets.length} markets)`);
    }
    
    if (!nextCursor || markets.length === 0) {
      console.log(`✅ Done! Fetched all ${page + 1} pages`);
      break;
    }
    cursor = nextCursor;
    
    // Small delay for rate limiting
    await new Promise(resolve => setTimeout(resolve, 50));
  }
  
  // CRITICAL: Sort by expiration date (soonest first)
  // Kalshi returns furthest-first, so we need to reverse sort
  allMarkets.sort((a, b) => {
    const dateA = a.expiration_time || a.close_time || a.latest_expiration_time || '9999-12-31';
    const dateB = b.expiration_time || b.close_time || b.latest_expiration_time || '9999-12-31';
    return dateA.localeCompare(dateB);
  });
  
  console.log(`📊 Total: ${allMarkets.length} markets, sorted by expiration (soonest first)`);
  
  // Show soonest expiring
  console.log('📊 Soonest expiring markets:');
  allMarkets.slice(0, 10).forEach((m, i) => {
    const exp = m.expiration_time?.slice(0, 10) || m.close_time?.slice(0, 10) || 'unknown';
    console.log(`  ${i + 1}. ${m.ticker.slice(0, 30)} | ${m.last_price}¢ | vol:${m.volume} | exp:${exp}`);
  });
  
  return allMarkets;
}

/**
 * Fetch a single market by ticker
 * Uses worker proxy to avoid CORS issues
 */
export async function fetchMarket(ticker: string): Promise<KalshiMarket | null> {
  const workerUrl = getWorkerUrl();
  const url = `${workerUrl}/api/public/markets/${ticker}`;
  
  const response = await fetch(url);
  
  if (response.status === 404) {
    return null;
  }
  
  if (!response.ok) {
    throw new Error(`Kalshi API error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return data.market || null;
}

/**
 * Fetch orderbook for a market
 * Uses worker proxy to avoid CORS issues
 */
export async function fetchOrderbook(ticker: string): Promise<{
  yes: { price: number; quantity: number }[];
  no: { price: number; quantity: number }[];
}> {
  const workerUrl = getWorkerUrl();
  const url = `${workerUrl}/api/public/markets/${ticker}/orderbook`;
  
  const response = await fetch(url);
  
  if (!response.ok) {
    throw new Error(`Kalshi API error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return {
    yes: data.orderbook?.yes || [],
    no: data.orderbook?.no || [],
  };
}

// ============================================================================
// AUTHENTICATED TRADING API (Requires Kalshi account)
// ============================================================================

/**
 * Authenticate with Kalshi (for live trading)
 * In LIVE mode, credentials are stored in Cloudflare Worker secrets
 * This function is called through the worker proxy
 */
export async function authenticate(credentials?: KalshiCredentials): Promise<AuthToken> {
  // In LIVE mode, authentication is handled by the worker using stored secrets
  // We just need to verify the worker is accessible
  if (typeof window !== 'undefined') {
    const workerUrl = getWorkerUrl();
    const healthUrl = `${workerUrl}/health`;
    
    try {
      const healthCheck = await fetch(healthUrl);
      if (!healthCheck.ok) {
        throw new Error('Worker proxy not available');
      }
      
      // Worker handles auth internally, so we create a mock token
      // The actual auth happens server-side in the worker
      authToken = {
        token: 'worker-authenticated',
        userId: 'worker-user',
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000),
      };
      
      return authToken;
    } catch (error) {
      throw new Error(`Worker proxy error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  // Fallback: direct authentication (for development/testing)
  if (!credentials) {
    throw new Error('Credentials required for direct authentication');
  }
  
  const response = await fetch(`${TRADING_BASE_URL}/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    throw new Error(`Authentication failed: ${response.status}`);
  }

  const data = await response.json();
  
  authToken = {
    token: data.token,
    userId: data.member_id,
    expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24 hours
  };

  return authToken;
}

/**
 * Get account balance (requires auth)
 * Uses worker proxy in LIVE mode
 */
export async function getBalance(useWorker: boolean = false): Promise<{ balance: number; available: number }> {
  if (!authToken && !useWorker) {
    throw new Error('Not authenticated');
  }

  let url: string;
  let headers: HeadersInit = {};
  
  if (useWorker && typeof window !== 'undefined') {
    url = `${getWorkerUrl()}/api/trading/portfolio/balance`;
  } else {
    url = `${TRADING_BASE_URL}/portfolio/balance`;
    headers = {
      'Authorization': `Bearer ${authToken!.token}`,
    };
  }

  const response = await fetch(url, { headers });

  if (!response.ok) {
    throw new Error(`Failed to get balance: ${response.status}`);
  }

  const data = await response.json();
  return {
    balance: data.balance / 100, // Kalshi uses cents
    available: data.payout / 100,
  };
}

/**
 * Place an order (requires auth)
 * Uses worker proxy in LIVE mode
 */
export async function placeOrder(params: {
  ticker: string;
  side: 'yes' | 'no';
  count: number;  // Number of contracts
  type: 'market' | 'limit';
  price?: number; // In cents for limit orders
}, useWorker: boolean = false): Promise<{ orderId: string; fills: number }> {
  if (!authToken && !useWorker) {
    throw new Error('Not authenticated');
  }

  const body: any = {
    ticker: params.ticker,
    action: 'buy',
    side: params.side,
    count: params.count,
    type: params.type,
  };

  if (params.type === 'limit' && params.price) {
    body.yes_price = params.side === 'yes' ? params.price : undefined;
    body.no_price = params.side === 'no' ? params.price : undefined;
  }

  let url: string;
  let headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  if (useWorker && typeof window !== 'undefined') {
    url = `${getWorkerUrl()}/api/trading/portfolio/orders`;
  } else {
    url = `${TRADING_BASE_URL}/portfolio/orders`;
    headers['Authorization'] = `Bearer ${authToken!.token}`;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Order failed: ${response.status} - ${error}`);
  }

  const data = await response.json();
  return {
    orderId: data.order?.order_id || 'unknown',
    fills: data.order?.count || 0,
  };
}

/**
 * Get current positions (requires auth)
 * Uses worker proxy in LIVE mode
 */
export async function getPositions(useWorker: boolean = false): Promise<Array<{
  ticker: string;
  position: number;
  averagePrice: number;
}>> {
  if (!authToken && !useWorker) {
    throw new Error('Not authenticated');
  }

  let url: string;
  let headers: HeadersInit = {};
  
  if (useWorker && typeof window !== 'undefined') {
    url = `${getWorkerUrl()}/api/trading/portfolio/positions`;
  } else {
    url = `${TRADING_BASE_URL}/portfolio/positions`;
    headers = {
      'Authorization': `Bearer ${authToken!.token}`,
    };
  }

  const response = await fetch(url, { headers });

  if (!response.ok) {
    throw new Error(`Failed to get positions: ${response.status}`);
  }

  const data = await response.json();
  return (data.market_positions || []).map((p: any) => ({
    ticker: p.ticker,
    position: p.position,
    averagePrice: p.resting_orders_count || 0,
  }));
}

/**
 * Check if we're authenticated
 */
export function isAuthenticated(): boolean {
  return authToken !== null && authToken.expiresAt > new Date();
}

/**
 * Log out
 */
export function logout(): void {
  authToken = null;
}
